import type { InputHTMLAttributes, ReactNode } from "react";
import type { UpdateAddressBody } from "@/types/api";

/** Shared account editor fields; the account address form and checkout compose these. */
export function EditField({
  label,
  value,
  onChange,
  children,
  ...props
}: {
  label: string;
  value?: string;
  onChange?: (value: string) => void;
  children?: ReactNode;
} & Omit<InputHTMLAttributes<HTMLInputElement>, "onChange" | "value">) {
  return (
    <label className="flex flex-col gap-5">
      <span className="font-mono uppercase text-12 text-[#7C7C7C]">
        {label}
      </span>
      {children ?? (
        <input
          type="text"
          value={value}
          onChange={(event) => onChange?.(event.target.value)}
          {...props}
          className="h-40 px-15 bg-white border border-[#ECECEC] text-black text-13 focus-visible:shadow-focus"
        />
      )}
    </label>
  );
}

export function AddressFields({
  value: form,
  onChange: setForm,
  usOnly = false,
}: {
  value: UpdateAddressBody;
  onChange: (value: UpdateAddressBody) => void;
  usOnly?: boolean;
}) {
  return (
    <>
      <EditField
        label="Address line 1"
        value={form.line1}
        onChange={(v) => setForm({ ...form, line1: v })}
        required
      />
      <EditField
        label="Address line 2 (optional)"
        value={form.line2 ?? ""}
        onChange={(v) => setForm({ ...form, line2: v })}
      />
      <div className="grid grid-cols-2 gap-15">
        <EditField
          label="City"
          value={form.city}
          onChange={(v) => setForm({ ...form, city: v })}
          required
        />
        <EditField
          label="State"
          value={form.state}
          onChange={(v) => setForm({ ...form, state: v.toUpperCase() })}
          required
          maxLength={2}
        />
      </div>
      <div className="grid grid-cols-2 gap-15">
        <EditField
          pattern={usOnly ? "[0-9]{5}(-[0-9]{4})?" : undefined}
          inputMode="numeric"
          autoComplete="postal-code"
          label="ZIP"
          value={form.postal_code}
          onChange={(v) => setForm({ ...form, postal_code: v })}
          required
        />
        {!usOnly && (
          <EditField
            label="Country"
            value={form.country ?? "US"}
            onChange={(v) => setForm({ ...form, country: v.toUpperCase() })}
            maxLength={2}
          />
        )}
      </div>
    </>
  );
}
